import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const c = await api.get(`/clients/${id}`);
        setClient(c.data);
        const ps = await api.get("/projects");
        setProjects(ps.data.filter((p) => p.client_id === id));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [id]);

  if (!client) return <div className="text-sm text-slate-500">Loading…</div>;

  const totalBudget = projects.reduce((s, p) => s + (p.budget_amount || 0), 0);
  const totalHours = projects.reduce((s, p) => s + (p.budget_hours || 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="client-detail-page">
      <Link to="/portal/clients" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4"/> Back to clients</Link>

      <div>
        <div className="eyebrow">{client.client_code} · {client.engagement_type || "Engagement"}</div>
        <h1 className="font-sans text-4xl mt-2">{client.client_name}</h1>
        {client.industry && <p className="text-slate-600 text-sm mt-1">{client.industry}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <div className="eyebrow">Identity</div>
          <Field label="PAN" value={client.pan}/>
          <Field label="GSTIN" value={client.gstin}/>
          <Field label="Industry" value={client.industry}/>
          <Field label="Engagement Type" value={client.engagement_type}/>
          <Field label="Status" value={client.status}/>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <div className="eyebrow">Contact</div>
          <Field label="Contact Person" value={client.contact_person}/>
          <Field label="Mobile" value={client.mobile}/>
          <Field label="Email" value={client.email}/>
          <Field label="Address" value={client.address}/>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <div className="eyebrow">Engagement Totals</div>
          <Field label="Projects" value={projects.length}/>
          <Field label="Budgeted Hours" value={totalHours}/>
          <Field label="Budgeted Amount" value={`₹${totalBudget.toLocaleString("en-IN")}`}/>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="client-projects-table">
        <div className="p-6 border-b border-slate-200 eyebrow">Projects ({projects.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Name</th><th className="text-left p-4">Type</th><th className="text-left p-4">Manager</th><th className="text-right p-4">Budget Hrs</th><th className="text-right p-4">Budget ₹</th><th className="text-left p-4">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {projects.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No projects for this client.</td></tr>}
            {projects.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50">
                <td className="p-4 font-mono text-xs">{p.project_code}</td>
                <td className="p-4 font-medium"><Link to={`/portal/projects/${p.id}`} className="hover:text-[#946f30]">{p.project_name}</Link></td>
                <td className="p-4 text-slate-600">{p.project_type}</td>
                <td className="p-4 text-slate-600">{p.project_manager_name || "—"}</td>
                <td className="p-4 text-right serif-num">{p.budget_hours || 0}</td>
                <td className="p-4 text-right serif-num">₹{(p.budget_amount||0).toLocaleString("en-IN")}</td>
                <td className="p-4 text-xs uppercase tracking-[0.2em] text-slate-500">{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="text-sm text-slate-900 mt-1">{value || "—"}</div>
    </div>
  );
}
